/**
 * Important note: This component is way different from the other charts supported on the platform. While
 * Highcharts does appear to have a "stacked bars" chart available (https://www.highcharts.com/demo/bar-stacked),
 * it's extremely far from the result we're looking for here, and it seems like a huge time sink to use that as a
 * starting point.
 *
 * Current assumptions:
 * - The stacked bar will have at max 2 categories. Should be able to support more than 2 with a little effort.
 * - The chart does not require any sort of interactions - it's in a read-only state.
 *
 * If any of those assumptions become invalid, we'll have to strongly reconsider the simplistic approach used
 * by this component, and potentially go for a charts library that supports this type of chart.
 */

// Third-party
import React from 'react';

export type StackedBarSliceData = {
  value: number;
  color: string;
};

export type StackedBarData = {
  slices: StackedBarSliceData[];
  bounds?: {
    lower: React.ReactNode;
    upper: React.ReactNode;
  };
};

interface Props {
  data: StackedBarData;
  showLabels?: boolean;
}

const StackedBar = ({ data, showLabels = true }: Props) => {
  const getStyles = (slice: StackedBarSliceData) => ({
    width: `${slice.value}%`,
    backgroundColor: slice.color,
  });

  return (
    <div className="stacked-bar-wrapper">
      {data.bounds && showLabels && (
        <div className="labels">
          <span>{data.bounds.lower}</span>
          <span>{data.bounds.upper}</span>
        </div>
      )}

      <div className="stacked-bar">
        {data.slices
          .filter(slice => slice.value !== 0)
          .map((slice, index) => (
            <div key={index} style={getStyles(slice)} className="bar-section" />
          ))}
      </div>
    </div>
  );
};

export default StackedBar;
